import { useCallback, useEffect, useMemo, useState } from "react";
import { AccionValorView, AccionView, ClaseEdgeAccion, ClaseEdgeAccionValor, ClaseNodoDefinir, ClaseNodoDevolver, ClaseNodoEtapa, EtapaView, InterfazUpdateCoordenadas, PrefijoTipoEdge, PrefijoTipoNodo, TipoAccionId, TipoNodo } from "./types/types";
import ReactFlow, { Controls, Edge, MiniMap, Node, ReactFlowInstance, ReactFlowProvider, useEdgesState, useNodesState, useOnSelectionChange } from "reactflow";
import { Avatar, Button, Grid, Typography, useTheme } from "@mui/material";
import Icon from 'src/@core/components/icon'
import PageHeader from "src/@core/components/page-header";

import 'reactflow/dist/style.css';
import './styles/nodes-styles.css';

// Imports Nodos y Edges custom
import NodoEtapa from "./customNodes/NodoEtapa";
import NodoAccionPorValor from "./customNodes/NodoAccionPorValor";
import NodoAccionPorRetorno from "./customNodes/NodoAccionDeRetorno";
import { HttpActions, HttpActionsValues, HttpStages } from "../../http/workflow";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { z } from "src/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { AsyncSelect } from "src/components/forms";
import { HttpCacheWorkflow } from "src/http/workflow/httpCacheWorkflow";
import { EntityWithIdAndDescriptionFields, EntityWithIdFields } from "src/types";
import { SistemaPrincipalFields } from "src/types/workflow/cache";
import { aC } from "@fullcalendar/core/internal-common";

const schemaSistema = z.object({
    codSistemaPrincipal: z.number().optional(),
    codSistema: z.number().optional(),
})

type SchemaSistemaType = z.infer<typeof schemaSistema>

const VisualizadorPosta = () => {

    const theme = useTheme()

    const { control, watch, handleSubmit } = useForm<SchemaSistemaType>({
        resolver: zodResolver(schemaSistema)
    })

    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance>();

    const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);

    const [selectedNode, setSelectedNode] = useState<Node>();

    const [etapas, setEtapas] = useState<EtapaView[]>([]);
    const [acciones, setAcciones] = useState<AccionView[]>([]);
    const [accionesValor, setAccionesValor] = useState<AccionValorView[]>([]);

    const [sistemaId, setSistemaId] = useState<number>();

    const [loading, setLoading] = useState<boolean>(false);

    // Tipos de nodos.
    const nodeTypes = useMemo(() => ({
        [TipoNodo.ETAPA]: NodoEtapa,
        [TipoNodo.ACCION_VALOR]: NodoAccionPorValor,
        [TipoNodo.ACCION_RETORNO]: NodoAccionPorRetorno
    }), []);

    // Cuando cambia el sistema, se cargan las etapas, acciones y acciones por valor del nuevo sistema.
    useEffect(() => {

        if (!sistemaId) return;

        const etapas: EtapaView[] = [];
        const acciones: AccionView[] = [];
        const accionesValor: AccionValorView[] = [];

        setLoading(true);

        const promise = Promise.all([
            HttpStages.getStages(sistemaId),
            HttpActions.getActions(sistemaId),
            HttpActionsValues.getActionsValues(sistemaId)
        ])

        promise
            .then((res) => {
                console.log(res)
                etapas.push(...res[0]);
                acciones.push(...res[1]);
                accionesValor.push(...res[2]);

                setEtapas(etapas);
                setAcciones(acciones);
                setAccionesValor(accionesValor);
            })
            .catch((err) => {
                console.log(err);
            });

        toast.promise(promise, {
            loading: 'Cargando grafo',
            success: 'Cargado correctamente.',
            error: 'Error al cargar.'
        })

    }, [sistemaId])


    useEffect(() => {
        const labelStyle = { 
                fill: '#ffffff',
                fontSize: '0.4rem', 
                fontWeight: 600,
                fontFamily: 'Roboto, sans-serif'
            };
        
        const labelBgStyle = { 
                fill: theme.palette.primary.light,
                fillOpacity: 1,
                rx: 12,
                ry: 12
            };

        const labelBgPadding = [10, 8];

        const nodos: Node[] = [];
        const edges: Edge[] = [];

        etapas.forEach((etapa: EtapaView) => nodos.push(new ClaseNodoEtapa(etapa)));

        acciones.forEach((accion: AccionView) => {
            const origen = nodos.find((node: Node) => node.id === PrefijoTipoNodo.ETAPA + accion.idEtapaOrigen);
            const anchoTexto = accion.rotulo.length * 8;
            if (!origen) {
                console.log('No se encontró el nodo origen de la acción ' + accion.id);
            } else {
                switch (accion.codAccionTipo) {
                    case TipoAccionId.DEFINIR:
                        const nuevoNodoDefinir = new ClaseNodoDefinir(accion, origen, anchoTexto);
                        nuevoNodoDefinir.data.label = accion.rotulo;
                        nodos.push(nuevoNodoDefinir);
                        break;
                    case TipoAccionId.DEVOLVER:
                        nodos.push(new ClaseNodoDevolver(accion, origen));
                        break;
                    default:
                        break;
                }
            }

            const destino_nodo = nodos.find((node: Node) => node.id === PrefijoTipoNodo.DEFINIR + accion.id);
            const nuevoEdge = new ClaseEdgeAccion(accion, destino_nodo);
            nuevoEdge.label = accion.rotulo; 

            nuevoEdge.labelStyle = labelStyle;
            nuevoEdge.labelBgStyle = labelBgStyle;
            nuevoEdge.labelBgPadding = labelBgPadding;

            edges.push(nuevoEdge);
        });

        accionesValor.forEach((res: AccionValorView) => {
            const origen_accion = edges.find((edge: Edge) => edge.id === PrefijoTipoEdge.ACCION + res.idAccion);

            if (origen_accion) {
                const nuevoEdgeValor = new ClaseEdgeAccionValor(res, origen_accion);

                nuevoEdgeValor.label = res.rotulo; 
                nuevoEdgeValor.labelStyle = labelStyle;
                nuevoEdgeValor.labelBgStyle = labelBgStyle;
                nuevoEdgeValor.labelBgPadding = labelBgPadding;
                
                edges.push(nuevoEdgeValor);
            }

            // origen_accion && edges.push(new ClaseEdgeAccionValor(res, origen_accion));
        });

        setNodes(nodos);
        setEdges(edges);


    }, [etapas, acciones, accionesValor, setNodes, setEdges, reactFlowInstance])


    useEffect(() => {
        reactFlowInstance && setTimeout(reactFlowInstance.fitView);
        setLoading(false);
    }, [reactFlowInstance, nodes])


    useEffect(() => {
        const updatedEdges = edges.map((edge: Edge) => {
            if (edge.source == selectedNode?.id) {
                return {
                    ...edge,
                    animated: true,
                    style: { stroke: '#000c' },
                };
            } else {
                return {
                    ...edge,
                    animated: false,
                    style: { stroke: '#0003' },
                };
            }
        });

        setEdges(updatedEdges);
    }, [selectedNode]);

    const printCoordinatesOnSelect = useCallback(({ nodes }: { nodes: Node[] }) => {
        const selected = nodes[0];
        setSelectedNode(selected);
    }, [])

    useOnSelectionChange({
        onChange: printCoordinatesOnSelect
    })

    const alGuardar = useCallback(() => {

        if (!sistemaId) return;

        const nuevasCoordenadas: InterfazUpdateCoordenadas[] = [];

        if (reactFlowInstance) {

            reactFlowInstance.getNodes().forEach((node: Node) => {
                if (node.type === TipoNodo.ETAPA) {
                    nuevasCoordenadas.push({
                        id: parseInt(node.id.replace(PrefijoTipoNodo.ETAPA, '')),
                        posicionX: Math.round(node.position.x),
                        posicionY: Math.round(node.position.y)
                    })
                }
            });

            const promise = HttpStages.updateCoordinates(nuevasCoordenadas, sistemaId)

            promise
                .then()
                .catch(e => console.log(e))

            toast.promise(promise, {
                loading: 'Guardando',
                success: 'Guardado correctamente.',
                error: 'Error al guardar.'
            })
        }
    }, [reactFlowInstance, sistemaId]);

    const handleChange = (data: SchemaSistemaType) => {
        setSistemaId(data.codSistema)
    }

    const selectedCodSistemaPrincipal = watch('codSistemaPrincipal')
    const selectedCodSistema = watch('codSistema')

    useEffect(() => {
        if (!selectedCodSistema) return

        handleSubmit(handleChange)()
    }, [selectedCodSistema])

    const opcionesSistemaPrincipal = () => HttpCacheWorkflow.getSistemasPrincipales()
        .then(sis => (
            sis.map(opcion => (
                {
                    id: opcion[EntityWithIdFields.Id],
                    descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
                    icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{ height: '20px', width: '20px' }} />
                }
            ))
        ))

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <PageHeader
                        title={<Typography variant='h5'>Visualizador</Typography>}
                        subtitle={
                            <Typography variant='body2'>
                                Aquí puede visualizar un proceso. Seleccione el sistema para el cual desea verlo. Presione Guardar para registrar los cambios.
                            </Typography>
                        }
                    />
                </Grid>
                <Grid item xs={6} display={'flex'} alignItems={'center'}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <AsyncSelect
                                control={control}
                                name='codSistemaPrincipal'
                                label='País'
                                loadOptions={opcionesSistemaPrincipal}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <AsyncSelect
                                control={control}
                                name='codSistema'
                                label='Circuito'
                                loadOptions={
                                    selectedCodSistemaPrincipal ?
                                        (() => HttpCacheWorkflow.getSistemasPorSistemaPrincipal(selectedCodSistemaPrincipal)) :
                                        (() => undefined)
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Button variant='contained' onClick={alGuardar} color='primary' startIcon={<Icon icon='mdi:content-save' />} size='medium'>Guardar</Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                onInit={setReactFlowInstance}
                fitView
            >
                <Controls />
                <MiniMap />
                {loading && <></>}
            </ReactFlow>
        </>
    )
}

const VisualizadorPostaWithProvider = () => (
    <ReactFlowProvider>
        <VisualizadorPosta />
    </ReactFlowProvider>
)

export default VisualizadorPostaWithProvider;
